import { get } from 'lodash-es'

export const CLOCK_EDIT_REASONS = [
  { value: 'worker_forgot_to_clock', label: 'Worker forgot to clock in/out' },
  { value: 'clock_failed_app_or_device', label: 'Clock in/out failed (app or device issue)' },
  { value: 'worker_arrived_late_or_left_early', label: 'Worker arrived late / left early' },
  { value: 'worker_stayed_later_overtime', label: 'Worker stayed later (client-approved overtime)' },
  { value: 'break_time_correction', label: 'Break time correction' },
  { value: 'client_confirmed_different_hours', label: 'Client/manager confirmed different hours' },
  { value: 'worker_hours_dispute', label: 'Worker hours dispute / query' },
  { value: 'admin_data_entry_error', label: 'Admin correction (data-entry error)' },
] as const

export type ClockEditReason = (typeof CLOCK_EDIT_REASONS)[number]['value']

export type ClockFieldChange = {
  label: string
  previousValue?: string | null
  nextValue?: string | null
}

export type ClockEditBooking = {
  key: string
  bookingId: string | number
  workerName: string
  timeZone?: string
  changes: ClockFieldChange[]
}

export type ClockEditReasonsByBooking = Record<string, ClockEditReason>

type BookingEntity = {
  id: string | number
  full_name?: string
  timezone?: string
  worker?: {
    id?: string | number
    first_name?: string
    last_name?: string
  }
  [key: string]: unknown
}

type EntityMap = Record<string, BookingEntity>
type EntityMapChanges = Record<string, { update?: Record<string, unknown> }>

type AgencyClockTimeChanges = Record<
  string,
  Record<
    string,
    Record<
      string,
      {
        previousValue?: string | null
        nextValue?: string | null
      }
    >
  >
>

const WORKER_CLOCK_FIELDS = {
  'clock_in.time': 'Clock in',
  'clock_out.time': 'Clock out',
  'worker_time.clock_in': 'Worker clock in',
  'worker_time.clock_out': 'Worker clock out',
  'client_time.clock_in': 'Client clock in',
  'client_time.clock_out': 'Client clock out',
} as const

const AGENCY_CLOCK_FIELDS = {
  clock_in_time: 'Clock in',
  clock_out_time: 'Clock out',
} as const

const getWorkerName = (booking: BookingEntity) => {
  if (booking.full_name) return booking.full_name

  const worker = booking.worker ?? {}
  const name = [worker.first_name, worker.last_name].filter(Boolean).join(' ')

  return name || `Worker #${worker.id ?? 'unknown'}`
}

export const getWorkerClockEditBookings = (
  entityMapChanges: EntityMapChanges,
  entityMap: EntityMap,
  originalEntityMap: EntityMap,
): ClockEditBooking[] =>
  Object.entries(entityMapChanges).flatMap(([key, actions]) => {
    const updatedFields = actions.update ?? {}
    const currentBooking = entityMap[key]
    if (!currentBooking) return []

    const changes = Object.entries(WORKER_CLOCK_FIELDS).flatMap(([attr, label]) => {
      if (!Object.prototype.hasOwnProperty.call(updatedFields, attr)) return []

      return [
        {
          label,
          previousValue: get(originalEntityMap[key], attr) as string | null | undefined,
          nextValue: get(currentBooking, attr) as string | null | undefined,
        },
      ]
    })

    if (!changes.length) return []

    return [
      {
        key,
        bookingId: currentBooking.id,
        workerName: getWorkerName(currentBooking),
        timeZone: currentBooking.timezone,
        changes,
      },
    ]
  })

export const getAgencyClockEditBookings = (
  clockTimeChanges: AgencyClockTimeChanges,
  entityMap: Record<string, { agency_shift_bookings?: BookingEntity[] }>,
  shiftId: string | number,
  timeZone?: string,
): ClockEditBooking[] => {
  const shiftChanges = clockTimeChanges[String(shiftId)] ?? clockTimeChanges[shiftId] ?? {}
  const bookings =
    entityMap[String(shiftId)]?.agency_shift_bookings ?? entityMap[shiftId]?.agency_shift_bookings ?? []

  return Object.entries(shiftChanges).flatMap(([bookingKey, changedFields]) => {
    const booking = bookings.find(item => String(item.id) === bookingKey)
    if (!booking) return []

    const changes = Object.entries(AGENCY_CLOCK_FIELDS).flatMap(([attr, label]) => {
      const change = changedFields[attr]

      return change ? [{ label, ...change }] : []
    })

    if (!changes.length) return []

    return [
      {
        key: bookingKey,
        bookingId: booking.id,
        workerName: getWorkerName(booking),
        timeZone,
        changes,
      },
    ]
  })
}

export const addClockEditReasonsToChanges = (
  entityMapChanges: EntityMapChanges,
  reasons: ClockEditReasonsByBooking,
): EntityMapChanges =>
  Object.entries(reasons).reduce(
    (changes, [key, reason]) => ({
      ...changes,
      [key]: {
        ...changes[key],
        update: {
          ...changes[key]?.update,
          clock_edit_reason: reason,
        },
      },
    }),
    entityMapChanges,
  )
